import { useEffect, useState } from "react";
import { Menu, X, Languages } from "lucide-react";

const Header = ({ lang, setLang, t, onOpenConsult }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const navLinks = [
    { href: "#about", label: t.nav.about },
    { href: "#services", label: t.nav.services },
    { href: "#portfolio", label: t.nav.portfolio },
    { href: "#commerce", label: t.nav.commerce },
    { href: "#contact", label: t.nav.contact },
  ];

  const headerBg = scrolled
    ? "bg-white/95 backdrop-blur-xl border-b border-[#0a192f]/10 shadow-sm"
    : "bg-transparent";

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${headerBg}`}
    >
      <div className="bid-container flex items-center justify-between h-20">
        {/* Logo */}
        <a href="#top" className="flex items-center gap-3" data-testid="header-logo">
          <img
            src="/bid-logo.jpg"
            alt="BID Soluções Integradas"
            className="h-11 w-11 object-contain"
            onError={(e) => { e.target.style.display = "none"; }}
          />
          <div className="leading-none">
            <div className="font-display font-black text-[#0a192f] text-lg tracking-tight">
              BID<span className="text-[#d4af37]">.</span>
            </div>
            <div className="bid-overline text-[#4a5568] text-[10px] tracking-[0.18em]">
              Soluções Integradas
            </div>
          </div>
        </a>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-9">
          {navLinks.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-${l.href.slice(1)}`}
              className="font-display text-sm font-bold text-[#0a192f] uppercase tracking-wider hover:text-[#d4af37] transition-colors"
            >
              {l.label}
            </a>
          ))}
        </nav>

        {/* Right Actions */}
        <div className="flex items-center gap-3">
          <div
            className="hidden sm:flex items-center gap-1 border border-[#0a192f]/15 px-2 py-1"
            data-testid="lang-toggle"
          >
            <Languages className="w-3.5 h-3.5 text-[#4a5568] mr-1" />
            <button
              data-testid="lang-pt"
              onClick={() => setLang("pt")}
              className={`text-xs font-bold px-2 py-1 transition-colors ${lang === "pt" ? "bg-[#0a192f] text-white" : "text-[#0a192f] hover:bg-[#0a192f]/5"
                }`}
            >
              PT
            </button>
            <button
              data-testid="lang-en"
              onClick={() => setLang("en")}
              className={`text-xs font-bold px-2 py-1 transition-colors ${lang === "en" ? "bg-[#0a192f] text-white" : "text-[#0a192f] hover:bg-[#0a192f]/5"
                }`}
            >
              EN
            </button>
            <button
              data-testid="lang-fr"
              onClick={() => setLang("fr")}
              className={`text-xs font-bold px-2 py-1 transition-colors ${lang === "fr" ? "bg-[#0a192f] text-white" : "text-[#0a192f] hover:bg-[#0a192f]/5"
                }`}
            >
              FR
            </button>
          </div>

          <button
            onClick={onOpenConsult}
            data-testid="header-cta-btn"
            className="hidden md:inline-flex btn-primary text-xs px-5 py-3"
          >
            {t.nav.cta}
          </button>

          <button
            className="lg:hidden p-2 text-[#0a192f]"
            onClick={() => setOpen(!open)}
            aria-label="menu"
            data-testid="mobile-menu-btn"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div
          className="lg:hidden bg-white border-t border-[#0a192f]/10"
          data-testid="mobile-menu"
        >
          <div className="bid-container py-6 flex flex-col gap-4">
            {navLinks.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="font-display text-sm font-bold text-[#0a192f] uppercase tracking-wider py-2 border-b border-[#0a192f]/5"
              >
                {l.label}
              </a>
            ))}
            <div className="flex items-center gap-2 pt-2">
              <span className="bid-overline text-xs text-[#4a5568]">Idioma:</span>
              <button
                onClick={() => setLang("pt")}
                className={`text-xs font-bold px-3 py-1.5 ${lang === "pt" ? "bg-[#0a192f] text-white" : "border border-[#0a192f]/20"
                  }`}
              >
                PT
              </button>
              <button
                onClick={() => setLang("en")}
                className={`text-xs font-bold px-3 py-1.5 ${lang === "en" ? "bg-[#0a192f] text-white" : "border border-[#0a192f]/20"
                  }`}
              >
                EN
              </button>
              <button
                onClick={() => setLang("fr")}
                className={`text-xs font-bold px-3 py-1.5 ${lang === "fr" ? "bg-[#0a192f] text-white" : "border border-[#0a192f]/20"
                  }`}
              >
                FR
              </button>
            </div>
            <button
              onClick={() => { setOpen(false); onOpenConsult(); }}
              data-testid="mobile-cta-btn"
              className="btn-primary mt-2"
            >
              {t.nav.cta}
            </button>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
